import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Brain, TrendingUp, TrendingDown, AlertTriangle, Users, FileText,
  Sparkles, Activity, Loader2, ArrowRight, Wallet,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const eur = (n) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n || 0));

export default function BIDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/bi/dashboard").then(r => setData(r.data)).catch(e => setError(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="bi-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 flex items-center gap-2">
            <Brain className="w-3 h-3 text-amber-300" /> AI Business Intelligence
          </p>
          <h1 className="text-3xl font-display gradient-text-luxe">Ennusteet & Analytiikka</h1>
          <p className="text-white/60 text-sm mt-1">Automaattiset ennusteet liikevaihdosta, kassavirrasta ja asiakaskäyttäytymisestä.</p>
        </div>
      </header>

      {error && <div className="text-destructive">{error}</div>}
      {loading && <Loader2 className="w-6 h-6 animate-spin text-white/40" />}

      {data && (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="stat-block">
              <div className="flex items-center justify-between mb-2">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center text-emerald-300"><Wallet className="w-4 h-4" /></div>
                {data.growth_percent >= 0
                  ? <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                  : <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
              </div>
              <p className="text-[10px] uppercase tracking-widest text-white/40">Liikevaihto / kk</p>
              <p className="text-2xl font-display font-bold text-emerald-300">{eur(data.revenue_this_month)}</p>
              <p className={`text-xs ${data.growth_percent >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {data.growth_percent >= 0 ? "+" : ""}{data.growth_percent}% vs. edellinen
              </p>
            </div>
            <div className="stat-block">
              <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center text-amber-300 mb-2"><FileText className="w-4 h-4" /></div>
              <p className="text-[10px] uppercase tracking-widest text-white/40">Avoimet laskut</p>
              <p className="text-2xl font-display font-bold text-amber-300">{eur(data.open_invoices_total)}</p>
              <p className="text-xs text-white/40">{data.open_invoices_count} kpl</p>
            </div>
            <div className="stat-block">
              <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center text-blue-300 mb-2"><Users className="w-4 h-4" /></div>
              <p className="text-[10px] uppercase tracking-widest text-white/40">Asiakkaita</p>
              <p className="text-2xl font-display font-bold text-blue-300">{data.customers_count}</p>
            </div>
            <div className="stat-block">
              <div className="w-9 h-9 rounded-lg bg-violet-500/15 flex items-center justify-center text-violet-300 mb-2"><Sparkles className="w-4 h-4" /></div>
              <p className="text-[10px] uppercase tracking-widest text-white/40">AI-oivalluksia</p>
              <p className="text-2xl font-display font-bold text-violet-300">{(data.insights || []).length}</p>
            </div>
          </div>

          {/* AI insights */}
          {(data.insights || []).length > 0 && (
            <div className="space-y-2">
              <h2 className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-amber-400" /> Ai-oivallukset
              </h2>
              {data.insights.map((ins, i) => (
                <div key={i} className={`glass-card p-4 flex items-start gap-3`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center relative z-10 ${
                    ins.type === "positive" ? "bg-emerald-500/15 text-emerald-300" :
                    ins.type === "alert" ? "bg-red-500/15 text-red-300" :
                    "bg-amber-500/15 text-amber-300"
                  }`}>
                    {ins.type === "positive" ? <TrendingUp className="w-5 h-5" /> :
                     ins.type === "alert" ? <AlertTriangle className="w-5 h-5" /> :
                     <Brain className="w-5 h-5" />}
                  </div>
                  <div className="relative z-10">
                    <p className="font-medium text-white text-sm">{ins.title}</p>
                    <p className="text-xs text-white/60 mt-1">{ins.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Top customers */}
          <div className="glass-card p-6">
            <h2 className="text-base font-display text-white mb-4 relative z-10 flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-400" /> TOP-asiakkaat
            </h2>
            <div className="space-y-2 relative z-10">
              {(data.top_customers || []).map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center text-xs font-bold text-white">
                      {i + 1}
                    </div>
                    <div>
                      <p className="text-sm text-white/90">{c.name}</p>
                      <p className="text-xs text-white/40">{c.invoices} laskua</p>
                    </div>
                  </div>
                  <p className="text-sm font-mono text-amber-300">{eur(c.total)}</p>
                </div>
              ))}
              {(data.top_customers || []).length === 0 && <p className="text-white/40 text-sm">Ei asiakkaita vielä</p>}
            </div>
          </div>

          {/* Analyses list */}
          {(data.analyses || []).length > 0 && (
            <div className="glass-card p-6">
              <h2 className="text-base font-display text-white mb-4 relative z-10">Tallennetut ennusteet</h2>
              <div className="space-y-2 relative z-10">
                {data.analyses.map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                    <div>
                      <p className="text-sm text-white/90">{a.title}</p>
                      <p className="text-xs text-white/40">{a.period} · luottamus {Math.round((a.confidence || 0) * 100)}%</p>
                    </div>
                    <Badge className="bg-violet-500/20 text-violet-200 border-0 text-[10px]">{a.category}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
